/**
 * Guide Page Component
 * Purpose: Comprehensive guide for URL tracking, KPIs, and UTM parameters
 * Features: Google-style minimalistic design with responsive layout
 * Author: Traklet App
 * Created: 2025
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Analytics,
  TrendingUp,
  LocationOn,
  Devices,
  Campaign,
  Link as LinkIcon,
  CheckCircle,
  Info,
  Launch,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Guide: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const kpiMetrics = [
    {
      icon: <TrendingUp color="primary" />,
      title: 'Click-Through Rate (CTR)',
      description: 'Percentage of people who click your link after seeing it',
      formula: '(Total Clicks ÷ Total Impressions) × 100',
      importance: 'Measures link effectiveness and audience engagement'
    },
    {
      icon: <Analytics color="primary" />,
      title: 'Total Clicks',
      description: 'Raw number of times your shortened URL was clicked',
      formula: 'Sum of all click events',
      importance: 'Primary metric for measuring reach and popularity'
    },
    {
      icon: <LocationOn color="primary" />,
      title: 'Geographic Performance',
      description: 'Click distribution across different countries and regions',
      formula: 'Clicks grouped by geographic location',
      importance: 'Helps optimize content for specific markets'
    },
    {
      icon: <Devices color="primary" />,
      title: 'Device & Platform Analytics',
      description: 'Breakdown of clicks by device type and operating system',
      formula: 'Clicks categorized by user agent data',
      importance: 'Optimizes user experience for target devices'
    }
  ];

  const utmParameters = [
    {
      parameter: 'utm_source',
      description: 'Identifies the source of traffic',
      examples: ['google', 'facebook', 'newsletter', 'twitter'],
      required: true
    },
    {
      parameter: 'utm_medium',
      description: 'Identifies the marketing medium',
      examples: ['cpc', 'email', 'social', 'organic'],
      required: true
    },
    {
      parameter: 'utm_campaign',
      description: 'Identifies the specific campaign',
      examples: ['spring_sale', 'product_launch', 'holiday_promo'],
      required: true
    },
    {
      parameter: 'utm_term',
      description: 'Identifies paid search keywords',
      examples: ['running+shoes', 'best+laptop', 'cheap+flights'],
      required: false
    },
    {
      parameter: 'utm_content',
      description: 'Differentiates similar content or links',
      examples: ['banner_ad', 'text_link', 'button_cta'],
      required: false
    }
  ];

  const utmExamples = [
    {
      scenario: 'Email Newsletter',
      url: 'https://example.com/product?utm_source=newsletter&utm_medium=email&utm_campaign=weekly_deals',
      breakdown: {
        source: 'newsletter',
        medium: 'email',
        campaign: 'weekly_deals'
      }
    },
    {
      scenario: 'Facebook Ad',
      url: 'https://example.com/signup?utm_source=facebook&utm_medium=social&utm_campaign=spring_sale&utm_content=video_ad',
      breakdown: {
        source: 'facebook',
        medium: 'social',
        campaign: 'spring_sale',
        content: 'video_ad'
      }
    },
    {
      scenario: 'Google Ads',
      url: 'https://example.com/buy?utm_source=google&utm_medium=cpc&utm_campaign=brand_keywords&utm_term=best+shoes',
      breakdown: {
        source: 'google',
        medium: 'cpc',
        campaign: 'brand_keywords',
        term: 'best+shoes'
      }
    }
  ];

  const trackingBenefits = [
    'Measure ROI of marketing campaigns',
    'Identify top-performing traffic sources',
    'Optimize content for different audiences',
    'Track user journey across touchpoints',
    'Make data-driven marketing decisions',
    'Improve conversion rates',
    'Allocate budget to best-performing channels'
  ];

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 3, sm: 4, md: 6 }
      }}
    >
      {/* Hero Section */}
      <Stack 
        spacing={{ xs: 3, sm: 4 }} 
        alignItems="center" 
        textAlign="center" 
        sx={{ mb: { xs: 6, sm: 8 } }}
      >
        <Stack spacing={2} alignItems="center">
          <Typography
            variant={isMobile ? "h3" : "h2"}
            component="h1"
            sx={{
              background: 'linear-gradient(45deg, #3b82f6 30%, #8b5cf6 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700,
              textAlign: 'center'
            }}
          >
            URL Tracking & Analytics Guide
          </Typography>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            color="text.secondary"
            sx={{ 
              maxWidth: { xs: '100%', sm: 600, md: 800 }, 
              lineHeight: 1.6,
              fontWeight: 400
            }}
          >
            Master URL tracking with KPIs and UTM parameters to boost your marketing performance
          </Typography>
        </Stack>
        
        {!isAuthenticated && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              maxWidth: { xs: '100%', sm: 600 },
              width: '100%',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'info.light',
              backgroundColor: 'info.50'
            }}
          >
            <Stack spacing={2} alignItems="center">
              <Typography 
                variant="body1" 
                sx={{ fontWeight: 500, color: 'info.dark' }}
              >
                🚀 <strong>Ready to track your URLs?</strong> Register for free to access all analytics features!
              </Typography>
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                <Button
                  variant="contained"
                  onClick={() => navigate('/register')}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5
                  }}
                >
                  Register Free
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/login')}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 3,
                    py: 1.5
                  }}
                >
                  Login
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}
      </Stack>

      {/* KPI Metrics Section */}
      <Stack spacing={{ xs: 4, sm: 6 }} sx={{ mb: { xs: 6, sm: 8 } }}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Typography 
            variant={isMobile ? "h4" : "h3"} 
            component="h2" 
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            Key Performance Indicators (KPIs)
          </Typography>
          <Typography 
            variant={isMobile ? "body1" : "h6"} 
            color="text.secondary" 
            sx={{ maxWidth: 600, lineHeight: 1.6 }}
          >
            Essential metrics to track your URL performance
          </Typography>
        </Stack>
        
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {kpiMetrics.map((metric, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      {metric.icon}
                      <Typography 
                        variant={isMobile ? "h6" : "h5"} 
                        component="h3" 
                        sx={{ fontWeight: 600, color: 'text.primary' }}
                      >
                        {metric.title}
                      </Typography>
                    </Stack>
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      sx={{ lineHeight: 1.6 }}
                    >
                      {metric.description}
                    </Typography>
                    <Chip 
                      label={`Formula: ${metric.formula}`} 
                      variant="outlined" 
                      size={isMobile ? "small" : "medium"}
                      sx={{ 
                        alignSelf: 'flex-start',
                        fontWeight: 500,
                        borderRadius: 1.5
                      }} 
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'primary.main',
                        fontWeight: 500,
                        '& strong': { fontWeight: 600 }
                      }}
                    >
                      <strong>Why it matters:</strong> {metric.importance}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Stack>

      {/* UTM Parameters Section */}
      <Stack spacing={{ xs: 4, sm: 6 }} sx={{ mb: { xs: 6, sm: 8 } }}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Typography 
            variant={isMobile ? "h4" : "h3"} 
            component="h2" 
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            UTM Parameters Guide
          </Typography>
          <Typography 
            variant={isMobile ? "body1" : "h6"} 
            color="text.secondary" 
            sx={{ maxWidth: 600, lineHeight: 1.6 }}
          >
            Track your marketing campaigns with precision
          </Typography>
        </Stack>
        
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Stack spacing={3}>
            <Stack spacing={2}>
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                What are UTM Parameters?
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ lineHeight: 1.7 }}
              >
                UTM (Urchin Tracking Module) parameters are tags added to URLs that help you track 
                the effectiveness of marketing campaigns. They provide detailed insights into how 
                users find and interact with your content.
              </Typography>
            </Stack>
            
            {isMobile ? (
              <Stack spacing={2}>
                {utmParameters.map((param, index) => (
                  <Paper 
                    key={index}
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      borderRadius: 1.5,
                      backgroundColor: 'grey.50'
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip
                          label={param.parameter}
                          size="small"
                          sx={{ 
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            backgroundColor: 'grey.200'
                          }}
                        />
                        {param.required ? (
                          <Chip label="Required" color="error" size="small" />
                        ) : (
                          <Chip label="Optional" color="default" size="small" />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {param.description}
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {param.examples.map((example, i) => (
                          <Chip 
                            key={i} 
                            label={example} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Parameter</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Examples</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Required</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {utmParameters.map((param, index) => (
                      <TableRow 
                        key={index}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'grey.50'
                          }
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={param.parameter}
                            size="small"
                            sx={{ 
                              fontFamily: 'monospace',
                              fontWeight: 600,
                              backgroundColor: 'grey.100'
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {param.description}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {param.examples.map((example, i) => (
                              <Chip 
                                key={i} 
                                label={example} 
                                size="small" 
                                variant="outlined"
                                sx={{ fontSize: '0.75rem' }}
                              />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {param.required ? (
                            <Chip label="Required" color="error" size="small" />
                          ) : (
                            <Chip label="Optional" color="default" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </Paper>
      </Stack>

      {/* UTM Examples Section */}
      <Stack spacing={{ xs: 4, sm: 6 }} sx={{ mb: { xs: 6, sm: 8 } }}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Typography 
            variant={isMobile ? "h4" : "h3"} 
            component="h3" 
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            Real-World UTM Examples
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ maxWidth: 600, lineHeight: 1.6 }}
          >
            See how UTM parameters work in practice
          </Typography>
        </Stack>
        
        <Stack spacing={3}>
          {utmExamples.map((example, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }
              }}
            >
              <Stack spacing={3}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Campaign color="primary" />
                  <Typography 
                    variant={isMobile ? "h6" : "h5"} 
                    sx={{ fontWeight: 600, color: 'primary.main' }}
                  >
                    {example.scenario}
                  </Typography>
                </Stack>
                
                <Stack spacing={2}>
                  <Stack spacing={1}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                      Complete URL:
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        backgroundColor: 'grey.50',
                        borderRadius: 1.5,
                        overflow: 'auto'
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          wordBreak: 'break-all',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                      >
                        {example.url}
                      </Typography>
                    </Paper>
                  </Stack>
                  
                  <Stack spacing={1}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                      Parameter Breakdown:
                    </Typography>
                    <Stack 
                      direction="row" 
                      spacing={0.5} 
                      flexWrap="wrap" 
                      useFlexGap
                    >
                      {Object.entries(example.breakdown).map(([key, value]) => (
                        <Chip
                          key={key}
                          label={`${key}: ${value}`}
                          variant="outlined"
                          size="small"
                          sx={{
                            fontSize: '0.75rem',
                            fontFamily: 'monospace',
                            borderRadius: 1.5
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Stack>

      {/* Benefits Section */}
      <Stack spacing={{ xs: 4, sm: 6 }} sx={{ mb: { xs: 6, sm: 8 } }}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Typography 
            variant={isMobile ? "h4" : "h3"} 
            component="h2" 
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            Benefits of URL Tracking
          </Typography>
          <Typography 
            variant={isMobile ? "body1" : "h6"} 
            color="text.secondary" 
            sx={{ maxWidth: 600, lineHeight: 1.6 }}
          >
            Unlock powerful insights for your marketing campaigns
          </Typography>
        </Stack>
        
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Grid container spacing={{ xs: 3, md: 4 }}>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  sx={{ fontWeight: 600, color: 'text.primary' }}
                >
                  Why Track Your URLs?
                </Typography>
                <List sx={{ py: 0 }}>
                  {trackingBenefits.map((benefit, index) => (
                    <ListItem 
                      key={index} 
                      sx={{ 
                        py: 0.75,
                        px: 0,
                        '&:hover': {
                          backgroundColor: 'grey.50',
                          borderRadius: 1
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle 
                          color="success" 
                          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                        />
                      </ListItemIcon>
                      <ListItemText 
                        primary={benefit}
                        primaryTypographyProps={{
                          variant: 'body1',
                          sx: { 
                            fontWeight: 500,
                            color: 'text.primary',
                            lineHeight: 1.5
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Stack>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  sx={{ fontWeight: 600, color: 'text.primary' }}
                >
                  Traklet Analytics Features
                </Typography>
                <List sx={{ py: 0 }}>
                  <ListItem 
                    sx={{ 
                      py: 0.75,
                      px: 0,
                      '&:hover': {
                        backgroundColor: 'grey.50',
                        borderRadius: 1
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Analytics 
                        color="primary" 
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Real-time click tracking"
                      primaryTypographyProps={{
                        variant: 'body1',
                        sx: { 
                          fontWeight: 500,
                          color: 'text.primary',
                          lineHeight: 1.5
                        }
                      }}
                    />
                  </ListItem>
                  <ListItem 
                    sx={{ 
                      py: 0.75,
                      px: 0,
                      '&:hover': {
                        backgroundColor: 'grey.50',
                        borderRadius: 1
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <LocationOn 
                        color="primary" 
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Geographic analytics"
                      primaryTypographyProps={{
                        variant: 'body1',
                        sx: { 
                          fontWeight: 500,
                          color: 'text.primary',
                          lineHeight: 1.5
                        }
                      }}
                    />
                  </ListItem>
                  <ListItem 
                    sx={{ 
                      py: 0.75,
                      px: 0,
                      '&:hover': {
                        backgroundColor: 'grey.50',
                        borderRadius: 1
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Devices 
                        color="primary" 
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Device & platform insights"
                      primaryTypographyProps={{
                        variant: 'body1',
                        sx: { 
                          fontWeight: 500,
                          color: 'text.primary',
                          lineHeight: 1.5
                        }
                      }}
                    />
                  </ListItem>
                  <ListItem 
                    sx={{ 
                      py: 0.75,
                      px: 0,
                      '&:hover': {
                        backgroundColor: 'grey.50',
                        borderRadius: 1
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Campaign 
                        color="primary" 
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary="UTM parameter tracking"
                      primaryTypographyProps={{
                        variant: 'body1',
                        sx: { 
                          fontWeight: 500,
                          color: 'text.primary',
                          lineHeight: 1.5
                        }
                      }}
                    />
                  </ListItem>
                  <ListItem 
                    sx={{ 
                      py: 0.75,
                      px: 0,
                      '&:hover': {
                        backgroundColor: 'grey.50',
                        borderRadius: 1
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <TrendingUp 
                        color="primary" 
                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Performance trends"
                      primaryTypographyProps={{
                        variant: 'body1',
                        sx: { 
                          fontWeight: 500,
                          color: 'text.primary',
                          lineHeight: 1.5
                        }
                      }}
                    />
                  </ListItem>
                </List>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Stack>

      {/* Call to Action */}
      <Stack alignItems="center" textAlign="center">
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 4, sm: 6 },
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            borderRadius: 3,
            maxWidth: 600,
            width: '100%',
            mb: 8
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Typography 
              variant={isMobile ? "h4" : "h3"} 
              component="h2"
              sx={{ fontWeight: 600 }}
            >
              Start Tracking Your URLs Today
            </Typography>
            <Typography 
              variant={isMobile ? "body1" : "h6"} 
              sx={{ 
                opacity: 0.9,
                lineHeight: 1.6,
                maxWidth: 400
              }}
            >
              Join thousands of marketers using Traklet for powerful analytics
            </Typography>
            
            {!isAuthenticated ? (
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                alignItems="center"
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  sx={{
                    backgroundColor: 'white',
                    color: '#1976d2',
                    fontWeight: 600,
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1.25, sm: 1.5 },
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    '&:hover': {
                      backgroundColor: '#f8fafc',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                    },
                  }}
                >
                  Register Free
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    fontWeight: 500,
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1.25, sm: 1.5 },
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderColor: 'white'
                    },
                  }}
                >
                  Try Demo
                </Button>
              </Stack>
            ) : (
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/dashboard')}
                sx={{
                  backgroundColor: 'white',
                  color: '#1976d2',
                  fontWeight: 600,
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1.25, sm: 1.5 },
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '&:hover': {
                    backgroundColor: '#f8fafc',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                  },
                }}
              >
                Go to Dashboard
              </Button>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
};

export default Guide;